import { useState, useEffect, useRef, useMemo } from "react";
import { f7 } from "framework7-react";
import { saveLoginData } from '../utils/auth';
import useAuthenticate from '../actions/Authenticate';
import { useGlobalData } from '../context/GlobalDataContext';

/** Digits only, for API (e.g. "60" from "+60" or "60") */
const prefixToDigits = (prefix) => String(prefix ?? '').replace(/\D/g, '');

/** Shown beside the input; adds leading + when global omits it */
const formatPrefixForDisplay = (prefix) => {
    const s = String(prefix ?? '').trim();
    if (!s) return '';
    return s.startsWith('+') ? s : `+${s}`;
};

const SignupPopup = ({ onClose, onSwitchToLogin }) => {
    const { globalData } = useGlobalData();
    const { register, get_verify_code } = useAuthenticate();
    const loginMethodPhone = globalData?.settings?.login_method === 'phone';
    /** When unset, treat as required so existing backends keep SMS signup flow */
    const phoneVerificationRequired =
        globalData?.settings?.register_method?.tac_registration_required !== false;

    const [username, setUsername] = useState('');
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [phone, setPhone] = useState('');
    const rawGlobalPrefix = globalData?.settings?.phone_prefix;
    const phonePrefixList = useMemo(() => {
        if (Array.isArray(rawGlobalPrefix)) {
            return rawGlobalPrefix.filter(Boolean);
        }
        if (rawGlobalPrefix) {
            return [rawGlobalPrefix];
        }
        return [];
    }, [rawGlobalPrefix]);

    const [currentPrefix, setCurrentPrefix] = useState('');
    const [prefixDropdownOpen, setPrefixDropdownOpen] = useState(false);
    const prefixDropdownRef = useRef(null);

    useEffect(() => {
        if (phonePrefixList.length > 0) {
            setCurrentPrefix(phonePrefixList[0]);
        } else {
            setCurrentPrefix('');
        }
    }, [phonePrefixList]);
    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [isVerifying, setIsVerifying] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [errorField, setErrorField] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [smsSending, setSmsSending] = useState(false);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (prefixDropdownRef.current && !prefixDropdownRef.current.contains(e.target)) {
                setPrefixDropdownOpen(false);
            }
        };
        if (prefixDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [prefixDropdownOpen]);

    useEffect(() => {
        const scrollY = window.scrollY;
        document.body.classList.add('popup-open');
        document.documentElement.classList.add('popup-open');
        document.body.style.top = `-${scrollY}px`;

        return () => {
            const scrollY = document.body.style.top;
            document.body.classList.remove('popup-open');
            document.documentElement.classList.remove('popup-open');
            document.body.style.top = '';
            if (scrollY) {
                window.scrollTo(0, parseInt(scrollY || '0') * -1);
            }
        };
    }, []);

    const buildFullPhone = () => {
        const prefixDigits = prefixToDigits(currentPrefix) || '60';
        const localDigits = phone.replace(/\D/g, '').replace(/^0+/, '');
        return `${prefixDigits}${localDigits}`;
    };

    const validateConfirmPasswordOnBlur = () => {
        if (!confirmPassword.trim()) {
            if (errorField === 'confirmPassword') {
                setError('');
                setErrorField('');
            }
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setErrorField('confirmPassword');
            return;
        }
        if (errorField === 'confirmPassword') {
            setError('');
            setErrorField('');
        }
    };

    useEffect(() => {
        if (!isVerifying) return undefined;
        setCountdown(60);
        let remaining = 60;
        const timer = setInterval(() => {
            remaining -= 1;
            setCountdown(remaining);
            if (remaining <= 0) {
                clearInterval(timer);
                setIsVerifying(false);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [isVerifying]);

    const handleGetVerifyCode = async () => {
        const localDigits = phone.replace(/\D/g, '').replace(/^0+/, '');
        if (localDigits.length < 9) {
            setError('Please enter a valid phone number');
            setErrorField('phone');
            return;
        }
        setError('');
        setErrorField('');
        setSmsSending(true);
        const fullPhone = buildFullPhone();
        const res = await get_verify_code({ phone: fullPhone });
        setSmsSending(false);
        if (!res?.status) {
            setError(res?.message || 'Failed to send verification code');
            return;
        }
        setIsVerifying(true);
        if (typeof f7 !== 'undefined' && f7.toast) {
            f7.toast.create({ text: 'Verification code sent', closeTimeout: 2000 }).open();
        }
    };

    const handleCodeChange = (index, value) => {
        if (value.length <= 1 && /^\d*$/.test(value)) {
            const newCode = [...verificationCode];
            newCode[index] = value;
            setVerificationCode(newCode);
            if (value && index < 5) {
                const nextInput = document.querySelector(`.signup-popup-content input[data-verification-index="${index + 1}"]`);
                if (nextInput) nextInput.focus();
            }
        }
    };

    const handleCodeKeyDown = (e, index) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            if (verificationCode[index]) {
                const newCode = [...verificationCode];
                newCode[index] = '';
                setVerificationCode(newCode);
                return;
            }
            if (index > 0) {
                const prevInput = document.querySelector(`.signup-popup-content input[data-verification-index="${index - 1}"]`);
                if (prevInput) {
                    prevInput.focus();
                    const newCode = [...verificationCode];
                    newCode[index - 1] = '';
                    setVerificationCode(newCode);
                }
            }
        }
    };

    // Resend verification – Send SMS / resend code (commented out)
    // const handleSendSms = async () => {
    //     if (!phone.trim()) {
    //         setError('Please enter phone number');
    //         setErrorField('phone');
    //         return;
    //     }
    //     setError('');
    //     setErrorField('');
    //     setLoading(true);
    //     const prefixDigits = prefixToDigits(currentPrefix) || '60';
    //     const localDigits = phone.replace(/\D/g, '').replace(/^0+/, '');
    //     const fullPhone = `${prefixDigits}${localDigits}`;
    //     try {
    //         const res = await login({
    //             locale: 'en',
    //             device_type: '1',
    //             phone: fullPhone,
    //         });
    //         if (res && res.status) {
    //             setResendTimer(28);
    //             if (typeof f7 !== 'undefined' && f7.toast) {
    //                 f7.toast.create({ text: 'Verification code sent', closeTimeout: 2000 }).open();
    //             }
    //         } else {
    //             setResendTimer(28);
    //             if (typeof f7 !== 'undefined' && f7.toast) {
    //                 f7.toast.create({ text: res?.message || 'Code sent', closeTimeout: 2000 }).open();
    //             }
    //         }
    //     } catch (err) {
    //         setResendTimer(28);
    //         if (typeof f7 !== 'undefined' && f7.toast) {
    //             f7.toast.create({ text: 'Verification code sent', closeTimeout: 2000 }).open();
    //         }
    //     }
    //     setLoading(false);
    // };

    // const handleResendCode = async () => {
    //     if (resendTimer > 0) return;
    //     await handleSendSms();
    // };

    const handleSignup = async () => {
        setError('');
        setErrorField('');
        setSuccess('');

        if (!loginMethodPhone) {
            if (!username.trim()) {
                setError('Please enter username');
                setErrorField('username');
                return;
            }
            if (username.trim().length < 3) {
                setError('Username must be at least 3 characters');
                setErrorField('username');
                return;
            }
            if (/\s/.test(username)) {
                setError('Spaces are not allowed in the username');
                setErrorField('username');
                return;
            }
        }
        if (!fullName.trim()) {
            setError('Please enter full name');
            setErrorField('fullName');
            return;
        }
        if (!password) {
            setError('Please enter password');
            setErrorField('password');
            return;
        }
        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            setErrorField('password');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            setErrorField('confirmPassword');
            return;
        }
        const localDigits = phone.replace(/\D/g, '').replace(/^0+/, '');
        if (!localDigits.length) {
            setError('Please enter phone number');
            setErrorField('phone');
            return;
        }
        const fullPhone = buildFullPhone();
        const codeStr = verificationCode.join('');
        if (phoneVerificationRequired && (!codeStr || codeStr.length < 6)) {
            setError('Please enter the verification code');
            setErrorField('verificationCode');
            return;
        }

        const registerForm = {
            username: loginMethodPhone ? '' : username.trim(),
            full_name: fullName.trim(),
            password,
            password_confirmation: confirmPassword,
            phone: fullPhone,
        };
        if (phoneVerificationRequired) {
            registerForm.verify_code = codeStr;
        }

        setLoading(true);
        const result = await register(registerForm);
        setLoading(false);

        if (!result.status) {
            setError(result.message || 'Registration failed');
            return;
        }

        const user = result.data;
        if (user?.token) {
            const userData = {
                isLoggedIn: true,
                loginType: 'phone',
                identifier: phone,
                userId: user?.id ?? user?.user_id ?? user?.userId,
                username: user?.username ?? (loginMethodPhone ? '' : username),
                email: user?.email ?? '',
                phone: user?.phone ?? fullPhone,
                fullName: user?.full_name ?? user?.fullName ?? fullName,
            };
            saveLoginData(userData);
        }
        setSuccess('Sign up successful!');
        setTimeout(() => onClose(), 500);
    };

    return (
        <div className="signup-popup-overlay">
            <div className="signup-popup-content">
                <div className="signup-popup-header">
                    <h1 className="text-center white font-x-large font-bold">Sign Up</h1>
                    <div className="close-button" style={{ cursor: 'pointer' }} onClick={onClose}>
                        <i className="f7-icons font-bold white">xmark</i>
                    </div>
                </div>
                {error && (
                    <div className="signup-error-message text-center">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="signup-success-message text-center">
                        {success}
                    </div>
                )}
                <div className="signup-form">
                    {!loginMethodPhone && (
                        <div className={`form-group${errorField === 'username' ? ' has-error' : ''}`}>
                            <label htmlFor="username">Username</label>
                            <input type="text" id="username" autoComplete="username" value={username} onChange={(e) => { setUsername(e.target.value); if (errorField === 'username') setErrorField(''); }} />
                            <div className="full-name-warning font-large font-bold">
                                <span className="font-medium">Spaces are not allowed in the username</span>
                            </div>
                        </div>
                    )}

                    <div className={`form-group${errorField === 'fullName' ? ' has-error' : ''}`}>
                        <label htmlFor="full-name">Full Name</label>
                        <input type="text" id="full-name" value={fullName} onChange={(e) => { setFullName(e.target.value); if (errorField === 'fullName') setErrorField(''); }} />
                        <div className="full-name-warning font-large font-bold">
                            <span className="font-medium">Full name must be same as your bank account holder name for withdrawal, after register cannot be modified</span>
                        </div>
                    </div>

                    <div className={`form-group${errorField === 'password' ? ' has-error' : ''}`}>
                        <label htmlFor="password">Password</label>
                        <div className="password-input">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); if (errorField === 'password') setErrorField(''); }}
                            />
                            <div className="show-password-button" onClick={() => setShowPassword(!showPassword)}>
                                <i className="f7-icons font-x-large white">{showPassword ? 'eye_slash_fill' : 'eye_fill'}</i>
                            </div>
                        </div>
                    </div>

                    <div className={`form-group${errorField === 'confirmPassword' ? ' has-error' : ''}`}>
                        <label htmlFor="confirm-password">Confirm Password</label>
                        <div className="password-input">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirm-password"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    if (errorField === 'confirmPassword') {
                                        setErrorField('');
                                        if (error === 'Passwords do not match') setError('');
                                    }
                                }}
                                onBlur={validateConfirmPasswordOnBlur}
                            />
                            <div className="show-password-button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                                <i className="f7-icons font-x-large white">{showConfirmPassword ? 'eye_slash_fill' : 'eye_fill'}</i>
                            </div>
                        </div>
                    </div>

                    <div className={`form-group${errorField === 'phone' ? ' has-error' : ''}`}>
                        <label htmlFor="phone">Phone Number</label>
                        <div className="phone-input-wrapper">
                            <div className="phone-input-container" ref={prefixDropdownRef}>
                                <div className="phone-input">
                                    <div
                                        className="phone-input-country-code"
                                        onClick={() => phonePrefixList.length > 1 && setPrefixDropdownOpen(!prefixDropdownOpen)}
                                        role={phonePrefixList.length > 1 ? 'button' : undefined}
                                        tabIndex={phonePrefixList.length > 1 ? 0 : undefined}
                                        onKeyDown={(e) => {
                                            if (phonePrefixList.length > 1 && (e.key === 'Enter' || e.key === ' ')) {
                                                e.preventDefault();
                                                setPrefixDropdownOpen(!prefixDropdownOpen);
                                            }
                                        }}
                                        aria-expanded={phonePrefixList.length > 1 ? prefixDropdownOpen : undefined}
                                        aria-haspopup={phonePrefixList.length > 1 ? 'listbox' : undefined}
                                        style={{ cursor: phonePrefixList.length > 1 ? 'pointer' : 'default' }}
                                    >
                                        <span className="country-code white Inter font-large">
                                            {formatPrefixForDisplay(currentPrefix) || formatPrefixForDisplay('60')}
                                        </span>
                                        {phonePrefixList.length > 1 && (
                                            <div className={`dropdown-icon ${prefixDropdownOpen ? 'open' : ''}`}>
                                                <img src="/images/chevron_down.png" alt="" />
                                            </div>
                                        )}
                                    </div>
                                    {prefixDropdownOpen && phonePrefixList.length > 1 && (
                                        <>
                                            <div className="popup-flag-dropdown-overlay" onClick={() => setPrefixDropdownOpen(false)} aria-hidden="true" />
                                            <div className="popup-flag-dropdown" role="listbox">
                                                {phonePrefixList.map((prefix) => (
                                                    <div
                                                        key={String(prefix)}
                                                        className={`popup-flag-dropdown-item ${currentPrefix === prefix ? 'selected' : ''}`}
                                                        role="option"
                                                        aria-selected={currentPrefix === prefix}
                                                        onClick={() => { setCurrentPrefix(prefix); setPrefixDropdownOpen(false); }}
                                                    >
                                                        <span className="country-code">{formatPrefixForDisplay(prefix)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                    <input
                                        type="tel"
                                        id="phone"
                                        placeholder="1234567890"
                                        maxLength={15}
                                        pattern="[0-9]*"
                                        inputMode="numeric"
                                        value={phone}
                                        onChange={(e) => { setPhone(e.target.value); if (errorField === 'phone') setErrorField(''); }}
                                    />
                                </div>
                            </div>
                            {phoneVerificationRequired && (
                                <div className="signup-get-code-row">
                                    {!isVerifying && (
                                        <div
                                            className={`send-sms-button${smsSending ? ' disabled' : ''}`}
                                            onClick={smsSending ? undefined : handleGetVerifyCode}
                                            style={{ cursor: smsSending ? 'not-allowed' : 'pointer', opacity: smsSending ? 0.7 : 1 }}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (!smsSending && (e.key === 'Enter' || e.key === ' ')) {
                                                    e.preventDefault();
                                                    handleGetVerifyCode();
                                                }
                                            }}
                                        >
                                            <span className="font-large font-bold white">GET CODE</span>
                                        </div>
                                    )}
                                    {isVerifying && (
                                        <div className="signup-verify-countdown font-large font-bold white text-center">
                                            Countdown: {Math.floor(countdown / 60)}:
                                            {countdown % 60 < 10 ? `0${countdown % 60}` : countdown % 60}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {phoneVerificationRequired && (
                        <div className={`form-group${errorField === 'verificationCode' ? ' has-error' : ''}`}>
                            <label>Verification Code</label>
                            <div className="verification-hint font-medium white mb-1">
                                Press GET CODE to receive a verification code via SMS.
                            </div>
                            <div className="verification-input-container">
                                {verificationCode.map((digit, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => {
                                            handleCodeChange(index, e.target.value);
                                            if (errorField === 'verificationCode') setErrorField('');
                                        }}
                                        onKeyDown={(e) => handleCodeKeyDown(e, index)}
                                        data-verification-index={index}
                                        className="verification-input"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                <div className="signup-button-container">
                    <div
                        className={`signup-button${loading ? ' disabled' : ''}`}
                        onClick={loading ? undefined : handleSignup}
                        style={{ cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
                    >
                        <span className="font-large font-bold white">{loading ? 'Signing up...' : 'Sign Up'}</span>
                    </div>
                </div>
                <div className="remark-container">
                    <span className="font-large font-bold white text-center">
                        By clicking the <strong>REGISTER</strong> button, I hereby acknowledge that I am
                        above 18 years old and have read and accepted your <strong>Terms & Conditions</strong>
                    </span>
                </div>
                <div className="login-container">
                    <span>Already have an account? <span className="login-link font-large font-bold" style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={onSwitchToLogin || onClose}>Login</span></span>
                </div>
            </div>
        </div>
    )
}

export default SignupPopup;